import React from "react";
import { clsx } from "clsx";
import type { LucideIcon } from "lucide-react";

/** Glow accents — indigo / purple / pink family; light wells read on pale glass, dark wells on jet glass */
export type AnalyticsMetricGlow =
  | "indigo"
  | "purple"
  | "pink"
  | "cyan"
  | "violet"
  | "fuchsia";

/** Dark: opaque-ish neutral fill + white strokes (Lucide uses currentColor) — avoids light pastel bg + pale icon */
const GLOW_SHELL: Record<AnalyticsMetricGlow, string> = {
  indigo:
    "bg-indigo-100 text-indigo-700 ring-indigo-400/45 shadow-[0_0_22px_-4px_rgba(99,102,241,0.35)] dark:bg-neutral-950 dark:text-white dark:ring-indigo-400/75 dark:shadow-[0_0_22px_-2px_rgba(129,140,248,0.55)]",
  purple:
    "bg-purple-100 text-purple-800 ring-purple-400/45 shadow-[0_0_22px_-4px_rgba(168,85,247,0.35)] dark:bg-neutral-950 dark:text-white dark:ring-purple-400/75 dark:shadow-[0_0_22px_-2px_rgba(192,132,252,0.5)]",
  pink:
    "bg-pink-100 text-pink-800 ring-pink-400/45 shadow-[0_0_22px_-4px_rgba(236,72,153,0.32)] dark:bg-neutral-950 dark:text-white dark:ring-pink-400/75 dark:shadow-[0_0_22px_-2px_rgba(244,114,182,0.5)]",
  cyan:
    "bg-cyan-100 text-cyan-800 ring-cyan-400/50 shadow-[0_0_22px_-4px_rgba(34,211,238,0.3)] dark:bg-neutral-950 dark:text-white dark:ring-cyan-400/75 dark:shadow-[0_0_22px_-2px_rgba(34,211,238,0.5)]",
  violet:
    "bg-violet-100 text-violet-800 ring-violet-400/45 shadow-[0_0_22px_-4px_rgba(139,92,246,0.35)] dark:bg-neutral-950 dark:text-white dark:ring-violet-400/75 dark:shadow-[0_0_22px_-2px_rgba(167,139,250,0.5)]",
  fuchsia:
    "bg-fuchsia-100 text-fuchsia-800 ring-fuchsia-400/45 shadow-[0_0_22px_-4px_rgba(217,70,239,0.32)] dark:bg-neutral-950 dark:text-white dark:ring-fuchsia-400/75 dark:shadow-[0_0_22px_-2px_rgba(232,121,249,0.5)]",
};

export type AnalyticsGlassMetricCardProps = {
  icon: LucideIcon;
  label: string;
  value: React.ReactNode;
  glow: AnalyticsMetricGlow;
  /** Primary KPI — spans two columns on large screens */
  hero?: boolean;
  className?: string;
};

/**
 * Bubbly glass metric tile: translucent surface, gradient top accent (chrome palette), glowing icon well.
 */
export function AnalyticsGlassMetricCard({
  icon: Icon,
  label,
  value,
  glow,
  hero,
  className,
}: AnalyticsGlassMetricCardProps) {
  return (
    <div
      className={clsx(
        "relative flex flex-col overflow-hidden rounded-3xl border p-5 shadow-lg backdrop-blur-2xl transition duration-300",
        "border-slate-200/80 bg-white/75 shadow-slate-900/[0.06] ring-1 ring-white/90",
        "hover:border-purple-200/90 hover:shadow-purple-500/[0.08] hover:ring-purple-200/40",
        "dark:border-white/10 dark:bg-slate-950/65 dark:shadow-xl dark:shadow-black/45 dark:ring-white/[0.06]",
        "dark:hover:border-white/[0.14] dark:hover:shadow-purple-500/10 dark:hover:ring-purple-500/15",
        hero && "lg:col-span-2",
        className
      )}
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 opacity-95 dark:opacity-[0.92]"
        aria-hidden
      />
      <div className={clsx("relative flex gap-4", hero && "sm:flex-row sm:items-center")}>
        <div
          className={clsx(
            "flex h-14 w-14 shrink-0 items-center justify-center rounded-full ring-2 ring-inset [&_svg]:shrink-0",
            GLOW_SHELL[glow]
          )}
          aria-hidden
        >
          <Icon className="h-7 w-7 text-inherit" strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-white/50">
            {label}
          </p>
          <div
            className={clsx(
              "mt-2 font-black tracking-tight text-slate-900 dark:text-white",
              hero ? "text-4xl sm:text-5xl" : "text-3xl sm:text-4xl"
            )}
          >
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}
